/**
 * identity-brief.ts — Editorial copy layer for the User Analytics standfirst.
 *
 * Mirrors `posture-brief.ts` for the Identity pillar: composes the daily brief
 * (action-first headline, lede, primary action, verdict, provenance line) from the
 * active identity-pillar state. Voice = "trusted advisor": short sentences, no
 * em-dashes, named subjects ahead of row counts.
 *
 * Contract: every editorial slot is ONE deterministic, data-driven line.
 * There is no variant table, no rotation — the same input always
 * produces the same brief, and all variation in what the user reads comes
 * from the data (score, delta, critical count, top action), never from
 * picking among synonymous phrasings of the same fact.
 *
 * Headline precedence (highest to lowest, see `pickActionHeadline`):
 *   1. Named members — one or more members still need MFA.
 *   2. topAction     — the priority-actions ranker's #1 item, named directly.
 *   3. Clear (score >= 95) or remaining-members fallback.
 *
 * Verdict (six words or fewer) reads as: settled at the
 * top, a percentage gap to close in the high band, and the named member
 * count (or the lead item) below that.
 *
 * `criticalCount` (renamed `crit` internally) is only trusted when it is a
 * finite number >= 0 — an undefined or malformed count routes every slot to
 * its no-named-cohort fallback rather than claiming a count that isn't
 * there.
 *
 * Loading-state handling: `input.score == null` short-circuits to the
 * neutral "Reading adoption…" brief so a page that mounts before the score
 * query resolves doesn't flash an authoritative-but-wrong worst-band verdict.
 * With `noData` (no completed user scan) it says so instead: no number, no
 * all-clear, and for a never-assessed tenant the Command Center's
 * first-assessment line (`firstAssessment`).
 */

import {
  briefProvenance,
  cap,
  FIRST_ASSESSMENT,
  firstAssessmentAction,
  LEDE_FRAGMENTS,
  mfaLedeBody,
} from './brief-helpers'
import type { ActionBriefFields } from './brief-shape'

export type Pillar = 'compliance' | 'bpa' | 'identity'

export interface IdentityBrief {
  /** Six words or fewer. Not drawn: the Carbon pillar brief shows no verdict (D22). */
  verdict: string
  /** The provenance line (rule 4, D36): "Assessed by Guardian at 06:12 today", or "" while the time is unknown. Kept under its old name for PillarBrief. */
  byline: string
  /** Score delta vs N days ago, for PillarBrief's delta tag on the score tile (a page can hide it). */
  delta: { value: number; days: number }
  /** Action-first h1 (blueprint §6.2 row 3). */
  headline: string
  /** Never set by this composer: its headlines name members, not a pillar. */
  emphasis?: ActionBriefFields['emphasis']
  /** One sentence, one `**bold**` run naming the subject and its count. */
  lede: string
  /** The view's one Soft Green button; only with a `primaryHref` and a known count of members lacking MFA. */
  primaryAction?: ActionBriefFields['primaryAction']
}

/* -------------------------------------------------------------------------- */
/* Input shape                                                                */
/* -------------------------------------------------------------------------- */

export interface IdentityBriefInput {
  /** Signed-in user email. Unused since the byline became the provenance line
   *  (rule 5 bans bylines); kept for call-site compatibility. */
  userEmail?: string
  /** When Guardian last refreshed User Analytics (`lastRefreshedAt`): null
   *  when never, undefined while unknown. Feeds the provenance line. */
  assessedAt?: string | null
  /** The provenance clock's "today"; tests only. */
  now?: Date
  /** Active tenant ID. Callers still pass this; unused by the composer now
   *  that copy is deterministic rather than varied per tenant per day. */
  tenantId?: string
  /** MFA member adoption percent (0-100) — same number rendered on PillarIdentity. */
  score?: number | null
  /** 30-day score delta — drives the lede's delta clause and the `delta` field. */
  deltas?: { score?: number; days?: number }
  /** Highest-impact identity action — informs the headline and verdict. */
  topAction?: { pillar: Pillar; title: string; impact?: 'crit' | 'high' | 'med' }
  /** Count of people needing attention — drives the headline, lede, and
   *  verdict named-cohort clauses. Typically `noMfaEnabledUsers` + critical-tier
   *  user count. */
  criticalCount?: number
  /**
   * Enabled member Global Administrators without MFA (`UserInsights
   * .noMfaGlobalAdmins`). Adds the lede's Global admin clause when it is
   * between 1 and `criticalCount`; it counts Global Administrators only, so
   * the copy says "Global admins", never "admins" (§7 C3).
   */
  noMfaGlobalAdmins?: number
  /** Where the primary button goes (the composer owns no routes). Without it,
   *  or without a known count of members lacking MFA, the brief has no `primaryAction`. */
  primaryHref?: string
  /** True when the tenant has no completed user scan (`UserInsights.hasData`
   *  false), not while a query loads. Only read when `score` is null. */
  noData?: boolean
  /**
   * With `noData`, for a tenant Guardian has never assessed: what the viewer
   * can do about the first assessment, the Command Center's never-scanned
   * policy (`firstAssessmentAction`). Without it, the plain no-user-data line.
   */
  firstAssessment?: Parameters<typeof firstAssessmentAction>[0]
}

/* -------------------------------------------------------------------------- */
/* Helpers                                                                    */
/* -------------------------------------------------------------------------- */

/** Strip trailing sentence punctuation from an action title before it's
 *  re-embedded in a new sentence (avoids "Enforce MFA.. first."). */
function stripTrailingPunct(s: string): string {
  return s.replace(/[.,;:!?]+$/, '')
}

/** Word form for 1-9, digits at 10 and above. `0` and negatives never reach
 *  this helper — callers only pass it a count already known to be >= 1. */
function numberWord(n: number): string {
  const words = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine']
  return n >= 1 && n <= 9 ? words[n] : String(n)
}

/** Only a finite, non-negative number is trusted as a real member count.
 *  Anything else (undefined, NaN, negative) routes every slot to its
 *  no-named-cohort fallback rather than claiming a count that isn't there. */
function validCount(n: number | undefined): number | undefined {
  return typeof n === 'number' && Number.isFinite(n) && n >= 0 ? n : undefined
}

/** Round to one decimal, then render as an integer string when whole
 *  ("4") and one decimal place otherwise ("0.4", "2.5"). */
function formatDelta(x: number): string {
  const r = Math.round(x * 10) / 10
  return Number.isInteger(r) ? String(r) : r.toFixed(1)
}

/* -------------------------------------------------------------------------- */
/* Copy tables — one line per slot, tokens filled from data                   */
/* -------------------------------------------------------------------------- */

const VERDICT = {
  ROUTINE_WATCH: 'Routine watch only.',
  CLOSE_GAP: 'Close the last {gap}%.',
  MEMBERS_NEED: '{count} members still need MFA.',
  ONE_NEEDS: 'One member still needs MFA.',
  START_LEAD: 'Start with the lead item.',
  ENROL_REMAINING: 'Enrol the remaining members this sprint.',
  NO_DATA: 'No user data yet',
}

/**
 * Action-first h1 (blueprint §6.2 row 3). The members without MFA lead,
 * ahead of the ranker's top action: on this page that action restates the
 * same cohort ("Enroll 20 members on MFA"), so it speaks only when no member
 * is named. The fallbacks mirror the verdict's bands so the two never disagree.
 */
const HEADLINE_ACTION = {
  LOADING: 'Reading MFA adoption.',
  /** No completed user scan on a tenant Guardian has assessed. */
  NO_DATA: 'Check back after the first user scan.',
  ENROL_ONE: 'Enrol the member without MFA this week.',
  ENROL_FEW: 'Enrol the {n} members without MFA this week.',
  TOP_ACTION: '{title} first.',
  CLEAR: 'Nothing to enrol this week.',
  REMAINING: 'Enrol the remaining members this sprint.',
}

/** The primary button names the members the lede counts. */
const PRIMARY_ACTION = {
  MEMBERS_ONE: 'Review the member',
  MEMBERS_FEW: 'Review the {n} members',
}

/** The lede: the members without MFA (`LEDE_FRAGMENTS`), else the adoption figure. */
const LEDE = {
  LOADING: '**Guardian is reading your tenant**.',
  NO_DATA: '**Guardian has no user data for this tenant yet**, so MFA adoption shows here after the first user scan.',
  ADOPTION: '**MFA adoption is at {score}%**',
  ADOPTION_UP: ', up {n} {point} in {d} days',
  ADOPTION_DOWN: ', down {n} {point} in {d} days',
  ADOPTION_FLAT: ', unchanged in {d} days',
}

/* -------------------------------------------------------------------------- */
/* Verdict (six words or fewer)                                               */
/* -------------------------------------------------------------------------- */

function pickVerdict(
  score: number,
  crit: number | undefined,
  topAction: IdentityBriefInput['topAction'] | undefined,
): string {
  if (score >= 95) {
    // All-clear only when nothing open is named elsewhere in the brief
    // (Phase 2 coherence invariant): a known critical count or a lead item
    // outranks "Routine watch only."
    if (typeof crit === 'number' && crit >= 2) {
      const count = crit <= 9 ? numberWord(crit) : String(crit)
      return VERDICT.MEMBERS_NEED.replace('{count}', count)
    }
    if (crit === 1) return VERDICT.ONE_NEEDS
    if (topAction?.title) return VERDICT.START_LEAD
    return VERDICT.ROUTINE_WATCH
  }

  if (score >= 85) {
    const gap = Math.max(0, Math.round(100 - score))
    return VERDICT.CLOSE_GAP.replace('{gap}', String(gap))
  }

  if (typeof crit === 'number' && crit >= 2) {
    const count = crit <= 9 ? numberWord(crit) : String(crit)
    return VERDICT.MEMBERS_NEED.replace('{count}', count)
  }
  if (crit === 1) return VERDICT.ONE_NEEDS
  if (topAction?.title) return VERDICT.START_LEAD
  return VERDICT.ENROL_REMAINING
}

/* -------------------------------------------------------------------------- */
/* Action-first headline + lede (blueprint §6.2 row 3)                        */
/* -------------------------------------------------------------------------- */

function pickActionHeadline(
  crit: number | undefined,
  topAction: IdentityBriefInput['topAction'] | undefined,
  score: number,
): string {
  if (crit !== undefined && crit >= 2) return HEADLINE_ACTION.ENROL_FEW.replace('{n}', String(crit))
  if (crit === 1) return HEADLINE_ACTION.ENROL_ONE
  if (topAction?.title) return HEADLINE_ACTION.TOP_ACTION.replace('{title}', cap(stripTrailingPunct(topAction.title)))
  if (score >= 95) return HEADLINE_ACTION.CLEAR
  return HEADLINE_ACTION.REMAINING
}

function buildLede(
  crit: number | undefined,
  globalAdmins: number | undefined,
  score: number,
  delta: { dir: 'up' | 'down' | 'flat'; n: string; days: number } | undefined,
): string {
  const members = mfaLedeBody(crit, globalAdmins)
  if (members) return `${members}.`
  const head = LEDE.ADOPTION.replace('{score}', String(Math.round(score)))
  if (!delta) return `${head}.`
  const point = delta.n === '1' ? 'point' : 'points'
  const clause =
    delta.dir === 'up' ? LEDE.ADOPTION_UP : delta.dir === 'down' ? LEDE.ADOPTION_DOWN : LEDE.ADOPTION_FLAT
  return `${head}${clause.replace('{n}', delta.n).replace('{point}', point).replace('{d}', String(delta.days))}.`
}

/* -------------------------------------------------------------------------- */
/* Public API                                                                 */
/* -------------------------------------------------------------------------- */

/**
 * Compose an IdentityBrief from the active identity-pillar state. All inputs
 * are optional — the function degrades gracefully toward a plausible "no data"
 * brief rather than throwing.
 *
 * When `input.score` is null/undefined (e.g. the page mounted before the
 * score query resolved) we return a neutral "reading" brief rather than
 * synthesizing a worst-band verdict. Without this guard the composer would
 * coerce `null → 0`, which lands the loading state in the lowest editorial
 * band and shows the user an authoritative-but-wrong verdict for a fraction
 * of a second before the real data arrives.
 *
 * Note: `score === 0` is a legitimate real value (a brand-new tenant with
 * zero MFA adoption) and still routes through the normal band logic —
 * only `null`/`undefined` triggers the loading brief.
 */
export function composeIdentityBrief(input: IdentityBriefInput = {}): IdentityBrief {
  const deltaValue = typeof input.deltas?.score === 'number' ? input.deltas.score : 0
  const deltaDays = typeof input.deltas?.days === 'number' ? input.deltas.days : 30

  const byline = briefProvenance(input.assessedAt, { now: input.now })

  // No completed user scan: say so, with no number and no all-clear. A
  // never-assessed tenant gets the Command Center's first-assessment line.
  if (input.score == null && input.noData) {
    const action = input.firstAssessment
      ? firstAssessmentAction(input.firstAssessment)
      : { headline: HEADLINE_ACTION.NO_DATA }
    return {
      verdict: VERDICT.NO_DATA,
      byline,
      delta: { value: deltaValue, days: deltaDays },
      headline: action.headline,
      lede: LEDE.NO_DATA,
      ...(action.primaryAction ? { primaryAction: action.primaryAction } : {}),
    }
  }

  if (input.score == null) {
    return {
      verdict: 'Reading adoption…',
      byline,
      delta: { value: deltaValue, days: deltaDays },
      headline: HEADLINE_ACTION.LOADING,
      lede: LEDE.LOADING,
    }
  }

  const score = input.score
  const dir: 'up' | 'down' | 'flat' = deltaValue > 0 ? 'up' : deltaValue < 0 ? 'down' : 'flat'
  const n = formatDelta(Math.abs(deltaValue))
  const crit = validCount(input.criticalCount)

  const verdict = pickVerdict(score, crit, input.topAction)

  // The lede's delta clause speaks only for a real delta, never the 0 default.
  const deltaKnown = typeof input.deltas?.score === 'number'
  const headline = pickActionHeadline(crit, input.topAction, score)
  const lede = buildLede(crit, input.noMfaGlobalAdmins, score, deltaKnown ? { dir, n, days: deltaDays } : undefined)

  const href = input.primaryHref?.trim()
  const primaryAction =
    href && crit !== undefined && crit >= 1
      ? {
          label: crit === 1 ? PRIMARY_ACTION.MEMBERS_ONE : PRIMARY_ACTION.MEMBERS_FEW.replace('{n}', String(crit)),
          href,
        }
      : undefined
  return {
    verdict,
    byline,
    delta: { value: deltaValue, days: deltaDays },
    headline,
    lede,
    ...(primaryAction ? { primaryAction } : {}),
  }
}

/* -------------------------------------------------------------------------- */
/* Test-only exports                                                          */
/* -------------------------------------------------------------------------- */

/** Copy tables exposed for exact-string assertions in tests (and for a
 *  generic copy-lint sweep). Not part of the public runtime API; do not
 *  import from app code. */
export const __TEST_ONLY__ = {
  VERDICT,
  HEADLINE_ACTION,
  LEDE,
  LEDE_FRAGMENTS,
  PRIMARY_ACTION,
  FIRST_ASSESSMENT,
}
